import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import { useState, useEffect } from "react";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { CalendarDaysIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { dispatchWithToastPromise } from "../../../Utils";
import { serviceAgreementIntervals } from "./utils";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { updateServiceAgreementAsync } from "../../../../slices/service/agreements";
import PMListbox from "../../../utils/PMListbox";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { FieldHint } from "./field-hint";
import { AgreementFormErrorSummary } from "./AgreementFormErrorSummary";

const ServiceAgreementEditDateDialog = (props) => {
  const { open, onClose, agreement, onRefresh, type } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  }, [open]);

  let initialValues = {
    id: agreement?.id,
    submit: null,
  };

  if (type === "date") {
    initialValues = {
      ...initialValues,
      next_service_date: agreement?.next_service_date,
    };
  } else if (type === "interval") {
    initialValues = {
      ...initialValues,
      interval_id: agreement?.interval?.id,
    };
  }

  return (
    <PMDialog
      onClose={onClose}
      open={open}
      title={
        type === "date"
          ? t("service.agreements.next_date.edit_date_title")
          : t("service.agreements.next_date.edit_interval_title")
      }
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({})}
        onSubmit={async (values, helpers) => {
          try {
            // Wait for confirmed save before success / close (GOAL_01 §3.3).
            await dispatchWithToastPromise(
              dispatch(updateServiceAgreementAsync(values)),
              onRefresh,
              onClose
            );
            helpers.setStatus({ success: true });
          } catch (err) {
            console.error(err);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: err?.message });
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          if (isLoading) {
            return <ResourceLoading />;
          }
          return (
            <Form>
              <AgreementFormErrorSummary formik={formik} />
              <div className="mt-4 grid grid-cols-6 gap-6">
                {type === "date" && (
                  <div className="col-span-6">
                    <PMField
                      name="next_service_date"
                      type="date"
                      label={t("service.agreements.info.next_date")}
                      {...formik}
                    />
                  </div>
                )}

                {type === "interval" && (
                  <div className="col-span-6">
                    <PMListbox
                      label={t("Interval")}
                      name="interval_id"
                      options={serviceAgreementIntervals}
                      selected={formik.values.interval_id}
                      {...formik}
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={onClose}
                >
                  {t("Close")}
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={formik.submitForm}
                  disabled={formik.isSubmitting}
                >
                  {formik.isSubmitting ? t("Saving...") : t("Save")}
                </button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </PMDialog>
  );
};

export const ServiceAgreementChangeDateSection = (props) => {
  const { t } = useTranslation();
  const { item, onRefresh } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChangeType, setSelectedChangeType] = useState();

  const handleSelectChangeType = (type) => {
    setSelectedChangeType(type);
    setModalOpen(true);
  };

  return (
    <>
      <section className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
        <div className="border-b border-slate-100 bg-[#EAF3FD] px-5 py-4">
          <h3 className="flex items-center text-[15px] font-extrabold text-slate-900">
            {t("service.agreements.next_date.section_title")}
            <FieldHint content={t("service.agreements.next_date.section_hint")} />
          </h3>
          <p className="mt-0.5 text-sm text-slate-600">
            {t("service.agreements.next_date.section_help")}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 p-3">
          <button
            type="button"
            onClick={() => handleSelectChangeType("date")}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1463C7] px-3 py-3 text-sm font-semibold text-white hover:bg-[#0B3A78]"
          >
            <CalendarDaysIcon className="h-4 w-4" />
            {t("service.agreements.next_date.edit_date")}
          </button>
          <button
            type="button"
            onClick={() => handleSelectChangeType("interval")}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
          >
            <ArrowPathIcon className="h-4 w-4" />
            {t("service.agreements.next_date.edit_interval")}
          </button>
        </div>
      </section>

      <ServiceAgreementEditDateDialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedChangeType(null);
        }}
        type={selectedChangeType}
        agreement={item}
        onRefresh={onRefresh}
      />
    </>
  );
};
